import React from "react";
import { useCoAgent } from "@copilotkit/react-core";
import { useSearchParams } from "react-router-dom";
import { isEmpty } from "lodash";
import ReactEcharts from "echarts-for-react";
import jsonToCsvExport from "json-to-csv-export";

import Button from "components/common/Button";
import Paper from "components/common/Paper";
import { FORECAST_NEW_PRODUCT_STEP } from "./constant/ForecastNewProductStepConstant";
import { AgentType } from "enums/AgentTypeEnum";
import { ForecastNewProductContentProps } from "./types/ForecastNewProductStepForm";

type ForecastNewProductPlanResultGraphProps = {
    isEdit?: true;
    stepper?: any;
} & ForecastNewProductContentProps;

//TODO: handle error state
export default function ForecastNewProductPlanResultGraph(
    props: ForecastNewProductPlanResultGraphProps
) {
    const { stepper, isEdit, formik } = props;
    const [_, setSearchParams] = useSearchParams();

    const agent = useCoAgent<any>({
        name: AgentType.NewProductForecaster,
        config: {
            subgraphs: true,
            configurable: {
                ldbId: formik.values.ldbId,
                thread_id: formik.values.threadId,
            },
            limit: 50,
        },
    });

    const { state } = agent;
    const isEmptyState = isEmpty(state);

    const graphResult = state || {};

    const adjustedForecast = graphResult?.adjustedForecast || [];
    const zeroToThirtyDaysTotalSales = Math.round(
        adjustedForecast?.slice(0, 3)?.reduce((acc, curr) => acc + curr.sales, 0) || 0
    );
    const thirtyToSixtyDaysTotalSales = Math.round(
        adjustedForecast?.slice(3, 6)?.reduce((acc, curr) => acc + curr.sales, 0) || 0
    );
    const sixtyToNinetyDaysTotalSales = Math.round(
        adjustedForecast?.slice(6, 9)?.reduce((acc, curr) => acc + curr.sales, 0) || 0
    );

    const totalSales = Math.round(
        adjustedForecast?.reduce((acc, curr) => acc + curr.sales, 0) || 0
    );

    const summary = graphResult?.forecastSummary;

    const option = {
        grid: { top: 8, right: 0, bottom: 24, left: 0, containLabel: true },

        xAxis: {
            type: "category",
            data: adjustedForecast?.map((_, index) => `Wk ${index + 1}`),

            axisLabel: {
                color: "#64748B",
            },
            axisPointer: {
                show: true,
                lineStyle: {
                    color: "#004253",
                    width: 2,
                    type: "dashed",
                },
            },
            splitLine: {
                show: true,
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                color: "#64748B",
            },
        },
        axisTick: {
            show: false,
        },
        series: [
            {
                data: adjustedForecast?.map((item) => Math.round(item.sales)),
                type: "line",
                smooth: true,
                lineStyle: {
                    width: 2,
                    color: "#22D3EE",
                    type: "line",
                },
                itemStyle: {
                    color: "#FEFEFE",
                    shadowColor: "black",
                    shadowBlur: 1,
                },
                symbol: "circle",
                symbolSize: 1,
                emphasis: {
                    scale: 12,
                },
            },
        ],
        tooltip: {
            trigger: "axis",
            formatter: (params) => {
                return `${params[0].name}: ${new Intl.NumberFormat("en-US").format(
                    Math.round(params[0].value)
                )}`;
            },
        },
    };

    const totalPredictedSales =
        Math.round(totalSales || 0) *
        Math.round(Number(graphResult?.newProduct?.inventory?.salesPrice?.amount?.toFixed(0)) || 0);

    const handleExport = () => {
        const data = adjustedForecast?.map((item, index) => ({
            week: `Week ${index + 1}`,
            sales: Math.round(item.sales),
        }));

        jsonToCsvExport({ data, filename: "new-product-plan-forecast" });
    };

    return (
        <div className="p-[24px] bg-[#F8FAFC] rounded-[8px] border border-[#F1F5F9]">
            <div className="flex justify-between">
                <p className="font-rocGroteskMedium">Projected sales forecast</p>
                <button
                    disabled={isEmptyState}
                    className="font-rocGroteskMedium"
                    onClick={handleExport}
                >
                    <i className="ri-share-forward-line"></i> Export
                </button>
            </div>

            <Paper className="mt-[20px]">
                <p className="text-[#5A5A5A] font-rocGroteskMedium">
                    Predicted sales <span className="text-[12px]">(Units)</span>
                </p>

                <div className="flex justify-between gap-[16px] mt-[12px]">
                    {[
                        {
                            unit: new Intl.NumberFormat("en-US").format(zeroToThirtyDaysTotalSales),
                            period: "0 - 30 days",
                        },
                        {
                            unit: new Intl.NumberFormat("en-US").format(
                                thirtyToSixtyDaysTotalSales
                            ),
                            period: "30 - 60 days",
                        },
                        {
                            unit: new Intl.NumberFormat("en-US").format(
                                sixtyToNinetyDaysTotalSales
                            ),
                            period: "60 - 90 days",
                        },
                    ].map((predictedSale) => (
                        <div className="flex flex-col">
                            <p className="font-rocGroteskMedium text-[20px]">
                                {predictedSale.unit}
                            </p>
                            <p className="text-[#94A3B8] font-rocGroteskMedium text-[12px]">
                                {predictedSale.period}
                            </p>
                        </div>
                    ))}
                </div>

                <hr className="w-full my-[16px]" />

                <div className="flex flex-col">
                    <p className="font-rocGroteskMedium text-[20px]">
                        {new Intl.NumberFormat("en-US").format(totalSales)}
                    </p>
                    <p className="text-[#64748B] font-rocGroteskMedium text-[12px]">
                        Total sales demand
                    </p>
                </div>
            </Paper>

            <Paper className="mt-[24px]">
                <p className=" font-rocGroteskMedium">Forecast graph </p>

                <hr className="mt-[16px] mb-[20px]" />

                <div className="mb-[32px]">
                    <div className="flex items-center gap-1">
                        <div className="w-[10px] h-[10px] bg-[#22D3EE] rounded-full" />
                        <p className="text-[#64748B] font-rocGroteskMedium text-[12px]">
                            Total predicted sales
                        </p>
                    </div>

                    <p className="font-rocGroteskMedium text-[14px]">
                        ${new Intl.NumberFormat("en-US").format(totalPredictedSales)}
                    </p>
                </div>

                <div className="relative">
                    <ReactEcharts option={option} className="pl-4" />
                    <p className="text-[#64748B] text-[12px] font-rocGroteskMedium absolute -left-5 rotate-90 top-1/2 -translate-y-1/2">
                        Quantity
                    </p>

                    <p className="text-[#64748B] text-[12px] font-rocGroteskMedium text-center absolute bottom-0 w-full">
                        Timeline
                    </p>
                </div>
            </Paper>

            <Paper className="mt-[24px] border border-gradient-to-r from-[#62CC00] from-0% to-[#FFB902] to-100%">
                <div className="flex gap-2 items-center">
                    <i className="ri-sparkling-fill text-sm text-transparent bg-clip-text bg-gradient-to-tr  from-[#62CC00] from-0% to-[#FFB902] to-100%"></i>

                    <p className="text-[14px]  font-rocGroteskMedium text-transparent  bg-clip-text bg-gradient-to-tr from-[#62CC00] from-0% to-[#FFB902] to-100%">
                        AI generated summary of this forecast
                    </p>
                </div>{" "}
                <p className="text-[#64748B] font-rocGroteskMedium text-[12px]">{summary}</p>
            </Paper>

            <div className="mt-[32px] ">
                {isEdit && (
                    <Button
                        onClick={() => {
                            stepper.go(FORECAST_NEW_PRODUCT_STEP.PLAN_EDIT);
                            setSearchParams({ newProductPlanId: formik.values.newProductPlanId });
                        }}
                        disabled={isEmptyState}
                        btnText="Edit"
                        icon={<i className="ri-edit-line"></i>}
                        btnClassname="mx-auto !w-fit !bg-white rounded-full border border-[#CBD5E1] shadow-sm"
                        rightIcon
                    />
                )}
            </div>
        </div>
    );
}
